// hooks/useMediaFromFirestore.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { Firestore, collection, query, orderBy, onSnapshot, doc, updateDoc, DocumentSnapshot } from "firebase/firestore";
import { apiPostSignUrls } from "../services/apiService";

// Public GCS base URL - use this if your bucket is public
const GCS_PUBLIC_BASE = `https://storage.cloud.google.com/${process.env.REACT_APP_BUCKET_NAME || 'ai_conic_history'}`;

// Refresh signed URLs when they have this many seconds left (5 minutes safety margin)
const REFRESH_BEFORE_EXPIRY_SECONDS = 300;

// Helper to convert GCS object path to public URL (if bucket is public)
export function objectPathToPublicUrl(objectPath?: string | null): string | null {
  if (!objectPath) return null;
  if (!GCS_PUBLIC_BASE) return null;
  const obj = objectPath.startsWith("/") ? objectPath.slice(1) : objectPath;
  return `${GCS_PUBLIC_BASE.replace(/\/+$/, "")}/${obj}`;
}

/**
 * Toggle the `starred` field on a picture document in Firestore.
 * Shared across all project collaborators in real-time via onSnapshot.
 */
export async function togglePictureStarred(
  db: Firestore,
  projectId: string,
  threadId: string,
  pictureId: string,
  currentValue: boolean
): Promise<void> {
  const ref = doc(db, "projects", projectId, "threads", threadId, "pictures", pictureId);
  await updateDoc(ref, { starred: !currentValue });
}

// ---------------------------------------------------------------------------
// Generic groupBySeq — works for both IImage[] and IVideo[]
// ---------------------------------------------------------------------------

interface HasSeq { seq: number; op: string; created_at: string; created_by: string; meta: any; project_id: string; thread_id: string }

function groupBySeq<T extends HasSeq, G>(items: T[], wrapGroup: (seq: number, groupItems: T[]) => G): G[] {
  const seqMap = new Map<number, T[]>();
  items.forEach((item) => {
    const existing = seqMap.get(item.seq) || [];
    existing.push(item);
    seqMap.set(item.seq, existing);
  });
  return Array.from(seqMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([seq, groupItems]) => wrapGroup(seq, groupItems));
}

// Public — used by tests and other modules
export function groupImagesBySeq(images: IImage[]): IImageGroup[] {
  return groupBySeq(images, (seq, items) => ({
    seq, images: items, op: items[0].op, created_at: items[0].created_at,
    created_by: items[0].created_by, meta: items[0].meta,
    project_id: items[0].project_id, thread_id: items[0].thread_id,
  }));
}

function groupVideosBySeq(videos: IVideo[]): IVideoGroup[] {
  return groupBySeq(videos, (seq, items) => ({
    seq, videos: items, op: items[0].op, created_at: items[0].created_at,
    created_by: items[0].created_by, meta: items[0].meta,
    project_id: items[0].project_id, thread_id: items[0].thread_id,
  }));
}

// ---------------------------------------------------------------------------
// Generic media-from-Firestore hook factory
// ---------------------------------------------------------------------------

interface MediaHookConfig<TItem, TGroup, TMeta> {
  /** Firestore sub-collection name: "pictures" | "videos" */
  collectionName: string;
  /** Kind passed to apiPostSignUrls */
  signUrlKind: 'pictures' | 'videos';
  /** Field that holds the unique ID inside the metadata */
  idField: keyof TMeta & string;
  /** Extract raw metadata from a Firestore document */
  extractMetadata: (d: DocumentSnapshot) => TMeta;
  /** Build the final item from metadata + resolved URL info */
  buildItem: (meta: TMeta, projectId: string, threadId: string, url: string, thumbUrl?: string) => TItem;
  /** Build the public-URL variant of an item */
  buildPublicItem: (meta: TMeta & { gcs_object?: string; thumb_object?: string }, projectId: string, threadId: string) => TItem;
  /** Group items by seq */
  groupItems: (items: TItem[]) => TGroup[];
}

function useMediaFromFirestoreBase<TItem, TGroup, TMeta extends { gcs_object?: string; thumb_object?: string }>(
  config: MediaHookConfig<TItem, TGroup, TMeta>,
  db: Firestore,
  projectId: string | undefined,
  threadId: string | undefined,
  usePublicUrls: boolean = false,
) {
  const [items, setItems] = useState<TItem[]>([]);
  const [groups, setGroups] = useState<TGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [urlExpirationTime, setUrlExpirationTime] = useState<number | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const metadataRef = useRef<TMeta[]>([]);
  const projectIdRef = useRef<string>();
  const threadIdRef = useRef<string>();
  const signedUrlsCacheRef = useRef<Map<string, { url: string; thumb_url?: string; expiresAt: number }>>(new Map());
  const fetchVersionRef = useRef(0);

  const fetchSignedUrls = useCallback(async (forceRefresh: boolean = false) => {
    const metadata = metadataRef.current;
    const currentProjectId = projectIdRef.current;
    const currentThreadId = threadIdRef.current;
    const version = ++fetchVersionRef.current;

    if (!currentProjectId || !currentThreadId || metadata.length === 0) return;

    try {
      const now = Date.now();
      const needsSigning = metadata.filter((m) => {
        const id = (m as any)[config.idField];
        const cached = signedUrlsCacheRef.current.get(id);
        return forceRefresh || !cached || cached.expiresAt <= now;
      });

      if (needsSigning.length > 0) {
        const ids = needsSigning.map((m) => (m as any)[config.idField] as string);
        const response = await apiPostSignUrls(currentProjectId, currentThreadId, ids, config.signUrlKind);
        const newUrls = response.signed_urls || {};
        const expiresAt = now + 3600 * 1000;
        needsSigning.forEach((m) => {
          const id = (m as any)[config.idField] as string;
          signedUrlsCacheRef.current.set(id, {
            url: newUrls[id] || "",
            thumb_url: newUrls[`${id}_thumb`],
            expiresAt,
          });
        });
      }

      if (fetchVersionRef.current !== version) return;

      const builtItems = metadataRef.current.map((m) => {
        const id = (m as any)[config.idField] as string;
        const cached = signedUrlsCacheRef.current.get(id);
        return config.buildItem(m, currentProjectId, currentThreadId, cached?.url || "", cached?.thumb_url);
      });

      setItems(builtItems);
      setGroups(config.groupItems(builtItems));

      const nextExpiration = Array.from(signedUrlsCacheRef.current.values())
        .map((v) => v.expiresAt)
        .reduce((min, exp) => Math.min(min, exp), now + 3600 * 1000);
      setUrlExpirationTime(nextExpiration);

      const refreshIn = Math.max(nextExpiration - now - REFRESH_BEFORE_EXPIRY_SECONDS * 1000, 0);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (refreshIn > 0) {
        refreshTimerRef.current = setTimeout(() => fetchSignedUrls(true), refreshIn);
      }

      setLoading(false);
    } catch (err) {
      console.error(`Failed to get signed URLs for ${config.collectionName}:`, err);
      if (fetchVersionRef.current !== version) return;

      // Fallback: render with whatever is cached so new items aren't silently dropped
      const latestMeta = metadataRef.current;
      const pid = projectIdRef.current;
      const tid = threadIdRef.current;
      if (pid && tid && latestMeta.length > 0) {
        const fallback = latestMeta.map((m) => {
          const id = (m as any)[config.idField] as string;
          const cached = signedUrlsCacheRef.current.get(id);
          return config.buildItem(m, pid, tid, cached?.url || "", cached?.thumb_url);
        });
        setItems(fallback);
        setGroups(config.groupItems(fallback));
      }
      setError(err instanceof Error ? err.message : String(err));
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    if (!projectId || !threadId) {
      setItems([]);
      setGroups([]);
      setLoading(false);
      signedUrlsCacheRef.current.clear();
      metadataRef.current = [];
      projectIdRef.current = undefined;
      threadIdRef.current = undefined;
      return;
    }

    projectIdRef.current = projectId;
    threadIdRef.current = threadId;
    setLoading(true);
    setError(null);

    const q = query(
      collection(db, "projects", projectId, "threads", threadId, config.collectionName),
      orderBy("seq", "asc"),
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const metadata = snapshot.docs.map((d) => config.extractMetadata(d));
        metadataRef.current = metadata;

        if (metadata.length === 0) {
          setItems([]);
          setGroups([]);
          setLoading(false);
          signedUrlsCacheRef.current.clear();
          return;
        }

        if (usePublicUrls) {
          const publicItems = metadata.map((m) => config.buildPublicItem(m, projectId, threadId));
          setItems(publicItems);
          setGroups(config.groupItems(publicItems));
          setLoading(false);
        } else {
          await fetchSignedUrls(false);
        }
      },
      (err) => {
        setError(err.message || String(err));
        setLoading(false);
      },
    );

    return () => {
      unsubscribe();
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [db, projectId, threadId, usePublicUrls, fetchSignedUrls, config]);

  return { items, groups, loading, error, urlExpirationTime };
}

// ---------------------------------------------------------------------------
// Image hook
// ---------------------------------------------------------------------------

type ImageMeta = {
  picture_id: string; seq: number; op: string; created_at: string;
  gcs_object?: string; thumb_object?: string; meta: any; created_by: string; starred: boolean;
};

const IMAGE_CONFIG: MediaHookConfig<IImage, IImageGroup, ImageMeta> = {
  collectionName: "pictures",
  signUrlKind: "pictures",
  idField: "picture_id",
  extractMetadata: (d) => ({
    picture_id: d.id,
    seq: d.get("seq"),
    op: d.get("op"),
    created_at: d.get("created_at")?.toDate()?.toISOString() ?? "",
    gcs_object: d.get("output_object"),
    thumb_object: d.get("thumb_object"),
    meta: d.get("meta") || {},
    created_by: d.get("created_by"),
    starred: d.get("starred") ?? false,
  }),
  buildItem: (m, projectId, threadId, url, thumbUrl) => ({
    picture_id: m.picture_id, project_id: projectId, thread_id: threadId,
    seq: m.seq, op: m.op, created_at: m.created_at, output_url: url,
    thumb_url: thumbUrl, created_by: m.created_by, meta: m.meta,
    expires_in_seconds: 3600, starred: m.starred,
  }),
  buildPublicItem: (m, projectId, threadId) => ({
    picture_id: m.picture_id, project_id: projectId, thread_id: threadId,
    seq: m.seq, op: m.op, created_at: m.created_at,
    output_url: objectPathToPublicUrl(m.gcs_object) || "",
    thumb_url: objectPathToPublicUrl(m.thumb_object) || undefined,
    meta: m.meta, created_by: m.created_by, expires_in_seconds: 3600, starred: m.starred,
  }),
  groupItems: groupImagesBySeq,
};

export function useImagesFromFirestore(
  db: Firestore, projectId: string | undefined, threadId: string | undefined, usePublicUrls: boolean = false,
) {
  const { items: images, groups: imageGroups, loading, error, urlExpirationTime } =
    useMediaFromFirestoreBase(IMAGE_CONFIG, db, projectId, threadId, usePublicUrls);
  return { images, imageGroups, loading, error, urlExpirationTime };
}

// ---------------------------------------------------------------------------
// Video hook
// ---------------------------------------------------------------------------

type VideoMeta = {
  video_id: string; seq: number; op: string; prompt?: string; created_at: string;
  gcs_object?: string; thumb_object?: string; status?: string; created_by: string; meta: any;
};

const VIDEO_CONFIG: MediaHookConfig<IVideo, IVideoGroup, VideoMeta> = {
  collectionName: "videos",
  signUrlKind: "videos",
  idField: "video_id",
  extractMetadata: (d) => ({
    video_id: d.id,
    seq: d.get("seq"),
    op: d.get("op") || "generate-video",
    prompt: d.get("prompt"),
    created_at: d.get("created_at")?.toDate()?.toISOString() ?? "",
    gcs_object: d.get("output_object"),
    thumb_object: d.get("thumb_object"),
    status: d.get("status"),
    created_by: d.get("created_by"),
    meta: d.get("meta") || {},
  }),
  buildItem: (m, projectId, threadId, url, thumbUrl) => ({
    video_id: m.video_id, project_id: projectId, thread_id: threadId,
    seq: m.seq, op: m.op, created_at: m.created_at, output_url: url,
    thumb_url: thumbUrl, meta: m.meta, created_by: m.created_by,
    expires_in_seconds: 3600,
  }),
  buildPublicItem: (m, projectId, threadId) => ({
    video_id: m.video_id, project_id: projectId, thread_id: threadId,
    seq: m.seq, op: m.op, created_at: m.created_at,
    output_url: objectPathToPublicUrl(m.gcs_object) || "",
    thumb_url: objectPathToPublicUrl(m.thumb_object) || undefined,
    meta: m.meta, created_by: m.created_by, expires_in_seconds: 3600,
  }),
  groupItems: groupVideosBySeq,
};

export function useVideosFromFirestore(
  db: Firestore, projectId: string | undefined, threadId: string | undefined, usePublicUrls: boolean = false,
) {
  const { items: videos, groups: videoGroups, loading, error, urlExpirationTime } =
    useMediaFromFirestoreBase(VIDEO_CONFIG, db, projectId, threadId, usePublicUrls);
  return { videos, videoGroups, loading, error, urlExpirationTime };
}
